import {
  Box,
  BoxContent,
  BoxSidebar,
  BoxSidebarItem,
  Heading,
} from '@mm/components';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import {
  activeChatbotSelector,
  connectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, Redirect, Route, Switch } from 'react-router-dom';
import useRouter from 'use-react-router';
import { usePhoneNumber } from '../../../../../common/hooks/use-phone-number';
import { TooltipGuard } from '../../../../../modules/permissions/RequiresPermissions';
import { NeedHelpLabel } from '../../../common/components/NeedHelpLabel/NeedHelpLabel';
import {
  SettingsSectionDefinition,
  SETTINGS_SECTIONS,
  SMS_SECTION_ID,
  USERS_SECTION_ID,
} from './sectionConstants';
import './Settings.pcss';
import { UserRoleContainer } from '../../../../../common/hooks/use-route-guard';

export const Settings = () => {
  const chatbotCRUDActions = useChatbotCRUDActions();
  const connectedPage = useSelector(connectedPageSelector);
  useEffect(() => {
    if (connectedPage) {
      chatbotCRUDActions.retrieveAll(connectedPage.id);
    }
    chatbotCRUDActions.getLanguages();
  }, []);

  const chatbot = useSelector(activeChatbotSelector);

  const { location } = useRouter();
  const { pathname } = location;

  const {
    roleFlags: { isFacebookPageCollaborator },
  } = UserRoleContainer.useContainer();

  const getIsActiveMenuItem = (
    pathname: string,
    section: SettingsSectionDefinition,
  ) => pathname.includes(section.path(chatbot.id));

  const { hasNumber } = usePhoneNumber();
  const sections = SETTINGS_SECTIONS.filter(({ id }) =>
    id === SMS_SECTION_ID
      ? hasNumber
      : id === USERS_SECTION_ID
      ? !isFacebookPageCollaborator
      : true,
  );

  return (
    <section className="settings">
      <Box className="settings__box">
        <BoxSidebar className="settings__sidebar">
          <BoxSidebarItem>
            <div className="settings__filter-heading-container">
              <Heading
                className="settings__filter-heading"
                text="Settings"
                size={5}
              />
              <NeedHelpLabel
                iconOnly={true}
                link="https://customers.ai/help/article/hoo6sxecqg-settings"
              />
            </div>
            <div className="settings__sidebar">
              {sections.map(section => {
                const isActive = getIsActiveMenuItem(pathname, section);

                const isAds = section.id.toLowerCase() === 'advertising';

                return (
                  <TooltipGuard
                    guardIf={isAds}
                    key={section.id}
                    position="right"
                    classNameArrow="settings__tooltip-guard-arrow"
                  >
                    {isAllowed => (
                      <Link
                        onClick={e => {
                          if (!isAllowed) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        to={section.path(chatbot.id)}
                        className={classnames('settings__sidebar-link', {
                          'settings__sidebar-link-active': isActive,
                        })}
                      >
                        {section.name}
                      </Link>
                    )}
                  </TooltipGuard>
                );
              })}
            </div>
          </BoxSidebarItem>
        </BoxSidebar>
        <BoxContent className="settings__box-content">
          <Switch>
            {sections.map(section => (
              <Route
                key={section.id}
                path={section.path(chatbot.id)}
                component={section.component}
              />
            ))}

            <Redirect to={sections[0].path(chatbot.id)} />
          </Switch>
        </BoxContent>
      </Box>
    </section>
  );
};
